import { CreatorActionType, UUID } from './types';
import { Attribute } from '@/api/AttributeApi';
import { AttributeValue } from '@/api/AttributeValueApi';
import { Component } from '@/api/ComponentApi';
import { ComponentVariation } from '@/api/ComponentVariationApi';
import { ConfigurationArticle, SelectedMontageType } from '@/api/ProjectApi';
import { ProjectTemplate } from '@/api/ProjectTemplateApi';
import ProductTypeApi, {
	ProductType,
	RollerBoxDepth,
	RollerBoxHeight,
	RollerShaftDiameter,
} from '@/api/ProductTypeApi';

export enum CreatorStep {
	ATTRIBUTE_VALUES,
	DIMENSIONS,
	COLORS,
	SUMMARY,
}

export type CreatorInitialData = Awaited<
	ReturnType<(typeof ProductTypeApi)['getCreatorInitialData']>
>;

export type CreatorAttributeValue = AttributeValue & {
	disabled: boolean;
};

export type AvailableAttributes = Record<UUID, CreatorAttribute[]>;

export type CreatorAttribute = Attribute & {
	attributeValues: AttributeValue[];
	filteredValues: CreatorAttributeValue[];
};

export interface CreatorSegment {
	id: UUID;
	width: number;
	isArmorError: boolean;
	isBelowMinRecommendedWidth: boolean;
	rollerBox: CreatorRollerBox;
	window: CreatorWindow;
	nonPerforatedSlatCount: number;
}

export interface CreatorWindow {
	id: UUID;
	height: number;
	wings: CreatorWing[];
	wingDivisions: WingDivisionType[];
}

export interface CreatorRollerBox {
	id: UUID;
}

export interface CreatorWing {
	id: UUID;
	width: number;
	isMosquitoError: boolean;
	withMosquitoNet: boolean;
}

export type CreatorProject = {
	description: string;
	componentVariations: ComponentVariation[];
	quantity: number;
	selectedMontageType: SelectedMontageType;
	configurationArticles: ConfigurationArticle[];
};

export type CreatorProjectTemplate = Pick<ProjectTemplate, 'name'>;

export interface CreatorComponent extends Component {
	variations: ComponentVariation[];
}

export interface ProjectDimensions {
	width: number;
	height: number;
	rollerBoxHeight: RollerBoxHeight;
	rollerBoxDepth: RollerBoxDepth;
	shaftDiameter: RollerShaftDiameter;
	segmentsQuantity: number;
}

export interface CreatorOptions {
	isReadOnly: boolean;
	isTemplate: boolean;
	currentStep: CreatorStep;
}

export interface ConfigurationConfig {
	project: {
		minWidth: number;
		maxWidth: number;
		minHeight: number;
		maxHeight: number;
		allowMosquitoNet: boolean;
		withArmor: boolean;
		maxShellsQuantityFormula: string;
	};
	segment: {
		minWidth: number;
		maxWidth: number;
		recommendedMinWidthRules: [
			{ value: number; withMosquitoNet: false },
			{ value: number; withMosquitoNet: true },
		];
		recommendedMinWidth: number;
		recommendedMaxWidth: number;
	};
	window: {
		minHeight: number;
	};
	rollerBox: {
		availableHeights: RollerBoxHeight[];
		availableDepths: RollerBoxDepth[];
	};
	wing: {
		minWidth: number;
		maxWidth: number;
		mosquitoNetMaxWidth: number;
	};
	constraints: Record<
		CreatorActionType,
		Record<
			UUID,
			| number
			| boolean
			| {
					withArmor: boolean;
					maxShellsQuantityFormula: string;
			  }
			| {
					value: number;
					withMosquitoNet: boolean;
			  }
		>
	>;
}

export enum SegmentDivisionType {
	NONE = 'NONE',
	THIN = 'THIN',
	WIDE = 'WIDE',
}

export enum WingDivisionType {
	NONE = 'NONE',
	PRESENT = 'PRESENT',
}

export interface SegmentJoint {
	divisionType: SegmentDivisionType;
	driverJunction: boolean;
}

export interface CreatorState {
	projectId: UUID | null;
	productType: ProductType | null;
	creatorProject: CreatorProject;
	creatorProjectTemplate: CreatorProjectTemplate;
	creatorComponents: CreatorComponent[];
	configurationConfig: ConfigurationConfig;
	segmentJoints: SegmentJoint[];
	segments: CreatorSegment[];
	projectDimensions: ProjectDimensions;
	selectedValues: Record<UUID, AttributeValue | null>;
	availableAttributes: AvailableAttributes;
	options: CreatorOptions;
}

export interface SerializedCreator {
	configurationConfig: ConfigurationConfig;
	selectedValuesIds: Record<UUID, UUID | null>;
	segmentJoints: SegmentJoint[];
	projectDimensions: ProjectDimensions;
	segments: CreatorSegment[];
}
